import { FC } from "react";
import { useParams } from "react-router";
import { useSelector } from "react-redux";

import CardDailyModel from "../CardDailyModel/CardDailyModel";
import {
  selectByDateModelsSortedByDailyAverageScoresState,
  selectModelLoading,
  selectModelsState,
} from "../../store/selectors/models";
import {
  selectDailyAverageModelLoadingState,
  selectDailyAverageModelError,
} from "../../store/selectors/scores";
import CardDailyModelSkeleton from "../CardDailyModel/CardDailyModelSkeleton";
import DataLoadError from "../DataLoadError";
import { toPositiveInt } from "../../utils/toPositiveInt";
import { useAppDispatch } from "../../hooks";
import { fetchDailyAverageModel } from "../../store/scores/slice";

const DEFAULT_ERROR_MESSAGE = "Failed to load data";

const CardDailyModelWrapper: FC = () => {
  const { id, date } = useParams();
  const dispatch = useAppDispatch();
  const safeBrandId = toPositiveInt(id) ?? -1;
  const models = useSelector(selectByDateModelsSortedByDailyAverageScoresState(safeBrandId, date));
  const loading = useSelector(selectDailyAverageModelLoadingState);
  const error = useSelector(selectDailyAverageModelError);
  const modelsItems = useSelector(selectModelsState);
  const modelsListLoading = useSelector(selectModelLoading);

  const hasRouteParams = safeBrandId > 0 && Boolean(date);
  const listStillLoading = modelsItems === null || modelsListLoading;
  const scoresNotReady = loading === "idle" || loading === "loading";
  const derivedMissing = models == null;

  if (loading === "error") {
    return (
      <DataLoadError
        message={error ?? DEFAULT_ERROR_MESSAGE}
        onRetry={() => dispatch(fetchDailyAverageModel({ brand_id: safeBrandId, days: 16 }))}
      />
    );
  }

  if (
    hasRouteParams &&
    derivedMissing &&
    (scoresNotReady || (loading === "success" && listStillLoading))
  ) {
    return <CardDailyModelSkeleton />;
  }

  if (models == null || models.length === 0) return null;

  return (
    <>
      {models.map((model, index) => (
        <CardDailyModel key={model.id} model={model} index={index} />
      ))}
    </>
  );
};

export default CardDailyModelWrapper; 